import { format, parseISO } from 'date-fns';
import { formatTimeStampToHHmm, formatTimestampToDDMMYYYY } from './dateUtils.js';
import type { AvailableInstallationDatesResponseItem } from '../types/installationDatesCoCo.js';

// Parses a date string, e.g. an ISO timestamp from the API, returning undefined for missing or invalid dates
export const toTimestamp = (date?: string): number | undefined => {
  const timestamp = Date.parse(date ?? '');
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

// Local date and time, e.g. "28.9.2026 12:15", or undefined for missing or invalid dates
export const formatDateStringToDDMMYYYYHHmm = (date?: string): string | undefined => {
  const timestamp = toTimestamp(date);
  return timestamp === undefined
    ? undefined
    : `${formatTimestampToDDMMYYYY(timestamp)} ${formatTimeStampToHHmm(timestamp)}`;
};

const toDatePickerValue = (date: Date): string => format(date, 'dd.MM.yyyy');

export const normalizeDatePickerValue = (value: string): string => {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value);
  if (!match) {
    return value;
  }

  const date = new Date(Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1])));
  return date.getUTCFullYear() === Number(match[3]) &&
    date.getUTCMonth() === Number(match[2]) - 1 &&
    date.getUTCDate() === Number(match[1])
    ? toDatePickerValue(date)
    : value;
};

export const getAvailableDates = (availability: AvailableInstallationDatesResponseItem[]): string[] => {
  const dates = new Set<string>();

  availability.forEach(item => {
    item.availableTimeSlot.forEach(slot => {
      const startDateTime = slot.validFor?.startDateTime;
      if (startDateTime) {
        const date = parseISO(startDateTime);
        if (!Number.isNaN(date.getTime())) {
          dates.add(toDatePickerValue(date));
        }
      }
    });
  });

  return [...dates].sort((first, second) => {
    const firstDate = parseISO(first.split('.').reverse().join('-'));
    const secondDate = parseISO(second.split('.').reverse().join('-'));
    return firstDate.getTime() - secondDate.getTime();
  });
};

export const getInstallationTimeSlotIdsForDate = (
  availability: AvailableInstallationDatesResponseItem[],
  selectedDate: string
): Record<string, string> => {
  const normalizedSelectedDate = normalizeDatePickerValue(selectedDate);
  const timeSlotIds: Record<string, string> = {};

  availability.forEach(item => {
    const matchingSlot = item.availableTimeSlot.find(slot => {
      const startDateTime = slot.validFor?.startDateTime;
      if (!startDateTime) {
        return false;
      }
      const date = parseISO(startDateTime);
      return !Number.isNaN(date.getTime()) && toDatePickerValue(date) === normalizedSelectedDate;
    });
    if (matchingSlot?.id) {
      timeSlotIds[item.cartItemId] = matchingSlot.id;
    }
  });

  return timeSlotIds;
};
